import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { ProfileTabData } from '@/components/generator/ProfileTab';
import { useProjectCache } from '@/hooks/useProjectCache';
import type { CachedProjectData } from '@/hooks/useProjectCache';

// Re-export the type for convenience
export type { ProfileTabData } from '@/components/generator/ProfileTab';

interface HistoryCacheEntry {
  entries: unknown[];
  index: number | null;
}

interface ProfileTabsContextType {
  profileTabs: ProfileTabData[];
  activeTabId: string | null;
  devModeEnabled: boolean;
  editedMessages: Record<string, string>;
  messageNotesDrafts: Record<string, string>;
  historyCache: Record<string, HistoryCacheEntry>;
  addProfileTab: (tab: ProfileTabData) => void;
  removeProfileTab: (tabId: string) => void;
  updateProfileTab: (
    tabId: string,
    updates: Partial<ProfileTabData> | ((prev: ProfileTabData) => Partial<ProfileTabData>)
  ) => void;
  setActiveTabId: (tabId: string | null) => void;
  setDevModeEnabled: (enabled: boolean) => void;
  setEditedMessage: (messageId: string, content: string) => void;
  clearEditedMessage: (messageId: string) => void;
  setNoteDraft: (messageId: string, notes: string) => void;
  clearNoteDraft: (messageId: string) => void;
  setCachedHistory: (key: string, entries: unknown[], index: number | null) => void;
  fetchOrGetCachedProject: (projectName: string) => Promise<CachedProjectData | null>;
  invalidateProjectCache: (projectName?: string) => void;
}

const ProfileTabsContext = createContext<ProfileTabsContextType | undefined>(undefined);

interface ProfileTabsProviderProps {
  children: ReactNode;
}

export function ProfileTabsProvider({ children }: ProfileTabsProviderProps) {
  const [profileTabs, setProfileTabs] = useState<ProfileTabData[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [devModeEnabled, setDevModeEnabled] = useState(false);
  const [editedMessages, setEditedMessages] = useState<Record<string, string>>({});
  const [messageNotesDrafts, setMessageNotesDrafts] = useState<Record<string, string>>({});
  const [historyCache, setHistoryCache] = useState<Record<string, HistoryCacheEntry>>({});
  const { fetchOrGetCached, invalidate } = useProjectCache();

  const setCachedHistory = useCallback((key: string, entries: unknown[], index: number | null) => {
    setHistoryCache(prev => ({ ...prev, [key]: { entries, index } }));
  }, []);

  const setEditedMessage = useCallback((messageId: string, content: string) => {
    setEditedMessages(prev => ({ ...prev, [messageId]: content }));
  }, []);

  const clearEditedMessage = useCallback((messageId: string) => {
    setEditedMessages(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
  }, []);

  const setNoteDraft = useCallback((messageId: string, notes: string) => {
    setMessageNotesDrafts(prev => ({ ...prev, [messageId]: notes }));
  }, []);

  const clearNoteDraft = useCallback((messageId: string) => {
    setMessageNotesDrafts(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
  }, []);

  const addProfileTab = useCallback((tab: ProfileTabData) => {
    setProfileTabs(prev => [...prev, tab]);
    setActiveTabId(tab.id);
  }, []);

  const removeProfileTab = useCallback((tabId: string) => {
    setProfileTabs(prev => {
      const newTabs = prev.filter(t => t.id !== tabId);
      return newTabs;
    });
    setActiveTabId(prev => {
      if (prev === tabId) {
        return null; // Go back to main generator
      }
      return prev;
    });
  }, []);

  const updateProfileTab = useCallback((
    tabId: string,
    updates: Partial<ProfileTabData> | ((prev: ProfileTabData) => Partial<ProfileTabData>)
  ) => {
    setProfileTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      const patch = typeof updates === 'function' ? updates(tab) : updates;
      return { ...tab, ...patch };
    }));
  }, []);

  return (
    <ProfileTabsContext.Provider
      value={{
        profileTabs,
        activeTabId,
        devModeEnabled,
        editedMessages,
        messageNotesDrafts,
        historyCache,
        setCachedHistory,
        addProfileTab,
        removeProfileTab,
        updateProfileTab,
        setActiveTabId,
        setDevModeEnabled,
        setEditedMessage,
        clearEditedMessage,
        setNoteDraft,
        clearNoteDraft,
        fetchOrGetCachedProject: fetchOrGetCached,
        invalidateProjectCache: invalidate,
      }}
    >
      {children}
    </ProfileTabsContext.Provider>
  );
}

export function useProfileTabs() {
  const context = useContext(ProfileTabsContext);
  if (context === undefined) {
    throw new Error('useProfileTabs must be used within a ProfileTabsProvider');
  }
  return context;
}
